import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { Option } from "@/shared/components/molecules/OptionsSelect";
import {
  colorsKpi,
  configureExporting,
  generateYearsOptions,
  initializeAmChart,
  Language,
  months,
} from "@/shared/utils/chartUtils";
import { Branch } from "@/app/branchs/branchs.type";
import * as am5 from "@amcharts/amcharts5";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5xy from "@amcharts/amcharts5/xy";
import { Select } from "@/shared/components/organisms/Select";
import { useGetExpensesCategoryBranchQuery } from "@/app/kpi/kpi.api";
import ScrollableSelectContainer from "@/shared/components/organisms/ScrollableSelectContainer";
import { faLoader } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export type ExpensesBranchCategoryChartProps = {
  branches: Branch[];
};

const ExpensesBranchCategoryChart: React.FC<
  ExpensesBranchCategoryChartProps
> = ({ branches }) => {
  const [isLoading, setIsLoading] = useState(true);
  //const [branchSelected, setBranchSelected] = useState<Option>();
  const [branchesOptions, setBranchesOptions] = useState<Option[]>([]);
  //const [yearSelected, setYearSelected] = useState<Option>();
  //const [categorySelected, setCategorySelected] = useState<Option>();
  const [currencyEarningsTotal, setCurrencyEarningsTotal] = useState<Option>();
  const [currencyEarningsTotalOptions, setCurrencyEarningsTotalOptions] =
    useState<Option[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);
  const yearsOptions = generateYearsOptions(
    new Date().getFullYear() - 10,
    new Date().getFullYear()
  );

  const [branchSelected, setBranchSelected] = useState<Option>(() => {
    const saved = localStorage.getItem("branchSelected");
    return saved ? JSON.parse(saved) : undefined;
  });
  const [yearSelected, setYearSelected] = useState<Option>(() => {
    const saved = localStorage.getItem("yearSelected");
    return saved ? JSON.parse(saved) : undefined;
  });
  const [categorySelected, setCategorySelected] = useState<Option>(() => {
    const saved = localStorage.getItem("categorySelected");
    return saved ? JSON.parse(saved) : undefined;
  });

  const { data: dataChart } = useGetExpensesCategoryBranchQuery({
    filial: branchSelected?.id,
    currency_request: currencyEarningsTotal?.id,
    year: yearSelected?.id,
    category_request: categorySelected?.id,
  });

  useEffect(() => {
    if (branches) {
      setBranchesOptions(
        branches.map((branch: Branch) => ({
          id: branch.id,
          name: branch.branch,
        }))
      );
    }
  }, [branches]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();

    if (branchesOptions.length > 0 && !branchSelected) {
      setBranchSelected(branchesOptions[0]);
    }
    if (yearsOptions.length > 0 && !yearSelected) {
      const defaultYear = yearsOptions.find(year => year.id === currentYear);
      setYearSelected(defaultYear || yearsOptions[0]);
    }
    if (categoryOptions.length > 0 && !categorySelected) {
      setCategorySelected(categoryOptions[0]);
    } 
  }, [branchesOptions, yearsOptions, categoryOptions, branchSelected, yearSelected, categorySelected]);

  useEffect(() => {
    const systemLanguage: Language = navigator.language.slice(0, 2) as Language;
    const selectedMonths = months[systemLanguage] || months["en"];

    if (dataChart) {
      const transformDataChart: any[] = [];
      dataChart.data.forEach((item: any) => {
        for (let index = 0; index < 11; index++) {
          if (!transformDataChart[index]) {
            transformDataChart[index] = { month: "" };
          }
          transformDataChart[index]["month"] = selectedMonths[index];
          transformDataChart[index][item["fee"]] = item["data"][index];
          transformDataChart[index]["none"] = 0;
        }
      });

      const currencies = Object.keys(dataChart.currency).map((key) => ({
        id: key,
        name: dataChart.currency[key],
      }));

      const categories = Object.keys(dataChart.category).map((key) => ({
        id: dataChart.category[key].value,
        name: dataChart.category[key].label,
      }));

      setCategoryOptions(categories);
      setCurrencyEarningsTotalOptions(currencies);
      setCurrencyEarningsTotal({
        id: dataChart.currency_request,
        name: dataChart.currency_request.toUpperCase(),
      });

      const currency = dataChart.currency_request.toUpperCase();

      createExpensesCategoryChart(
        transformDataChart,
        colorsKpi,
        currency,
        dataChart
      );
    }
  }, [dataChart]);

  function createExpensesCategoryChart(
    data: any,
    colors: any,
    currency: any,
    globalData: any
  ) {
    am5.ready(function () {
      const root = initializeAmChart("chart_expenses_category");
  
      root.setThemes([am5themes_Animated.new(root)]);
  
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: undefined,
          wheelY: undefined,
          paddingLeft: 0,
          paddingRight: 0,
          layout: root.verticalLayout,
        })
      );
  
      if (root._logo) {
        root._logo.dispose();
      }

      if (!data || data.length === 0) {
        console.log("No data available, displaying message.");
        root.container.children.push(
          am5.Label.new(root, {
            text: "Información no disponible",
            fontSize: 20,
            fill: am5.color("#cbcac8"),
            centerX: am5.p50,
            centerY: am5.p50,
            x: am5.p50,
            y: am5.p50,
            verticalCenter: "middle",
          })
        );

        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
        return;
      }
  
      const legend = chart.children.push(
        am5.Legend.new(root, {
          centerX: am5.p50,
          x: am5.p50,
          height: am5.percent(30),
          width: am5.percent(80),
          layout: am5.GridLayout.new(root, {
            fixedWidthGrid: true,
          }),
        })
      );
  
      // Configurar las etiquetas de la leyenda para que sean de color gris
      legend.labels.template.setAll({
        fontSize: 10,
        fontWeight: "300",
        fill: am5.color(0x6b7280), // text-gray-500
      });
  
      const xRenderer = am5xy.AxisRendererX.new(root, {
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        minGridDistance: 10,
        minorGridEnabled: true,
      });
  
      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "month",
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        })
      );
  
      xRenderer.grid.template.setAll({
        location: 1,
        visible: false,
      });
  
      xAxis.data.setAll(data);
  
      // Configurar las etiquetas del eje X para que sean de color gris
      xAxis.get("renderer").labels.template.setAll({
        fontSize: "10px",
        fill: am5.color(0x6b7280), // text-gray-500
      });
  
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          calculateTotals: true,
          min: 0,
          extraMax: 0.1,
          renderer: am5xy.AxisRendererY.new(root, {
            strokeOpacity: 0.1,
          }),
        })
      );
  
      // Configurar las etiquetas del eje Y para que sean de color gris
      yAxis.get("renderer").labels.template.setAll({
        fontSize: "10px",
        fill: am5.color(0x6b7280), // text-gray-500
      });
  
      function makeSeries(
        name: any,
        fieldName: any,
        showTotal: any,
        color: any
      ) {
        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: name,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: fieldName,
            categoryXField: "month",
            stacked: true,
            stroke: am5.color(0xffffff),
          })
        );
  
        series.columns.template.setAll({
          tooltipText: "{name}, {categoryX}: {valueY}" + currency,
          width: am5.percent(90),
          fill: am5.color(color),
          tooltipY: 0,
          strokeOpacity: 0, // Quitar la línea del borde
          strokeWidth: 0,
        });
  
        if (showTotal) {
          series.columns.template.setAll({
            fillOpacity: 0,
            strokeOpacity: 0,
          });
  
          series.bullets.push(function (root, series, dataItem) {
            if (dataItem.get("valueYTotal") !== 0) {
              return am5.Bullet.new(root, {
                locationY: 0.5,
                sprite: am5.Label.new(root, {
                  text: "{valueYTotal}",
                  fill: am5.color(0x000000),
                  centerY: am5.p100,
                  centerX: am5.p50,
                  populateText: true,
                  fontSize: "0.8rem",
                }),
              });
            }
          });
        }
  
        series.data.setAll(data);
        series.appear();
  
        if (!showTotal) {
          legend.data.push(series);
        }
      }
  
      globalData.data.forEach((item: any, index: any) => {
        makeSeries(item.fee, item.fee, false, colors[index]);
      });
      makeSeries("", "none", true, "#000000");
  
      chart.appear(1000, 100);
  
      configureExporting(root, data);
    }); // end am5.ready()
  }

  return (
    <>
      {" "}
      <h2 className="text-center font-bold leading-6 text-gray-900">
        {t("user.views.main_title_expenses_total_category_month")}
      </h2>
      <ScrollableSelectContainer>
        <Select
          name="branch"
          selected={branchSelected}
          setSelected={setBranchSelected}
          options={branchesOptions}
          tittle={t("user.views.branch")}
        />
        <Select
          name="currency"
          selected={currencyEarningsTotal}
          setSelected={setCurrencyEarningsTotal}
          options={currencyEarningsTotalOptions}
          tittle={t("user.views.currency")}
        />
        <Select
          name="year"
          selected={yearSelected}
          setSelected={setYearSelected}
          options={yearsOptions}
          tittle={t("user.views.year")}
        />
        <Select
          name="category"
          selected={categorySelected}
          setSelected={setCategorySelected}
          options={categoryOptions}
          tittle={t("share.views.category")}
        />
      </ScrollableSelectContainer>
      <div style={{ position: "relative" }} className="xl:mt-24 md:mt-24 sm:mt-2">
        {isLoading && (
          <div className="flex justify-center items-center h-90 mt-20">
            <FontAwesomeIcon icon={faLoader} spin className="text-6xl" />
          </div>
        )}
        <div
          id="chart_expenses_category"
          style={{ width: "100%", height: "400px" }}
        ></div>
      </div>
    </>
  );
};

export default ExpensesBranchCategoryChart;
